import type { HullPaintsFilters, HullPaintsPaint, HullPaintsVessel } from "../models/hull-paints.models";

export const EMPTY_HULL_PAINTS_FILTERS: HullPaintsFilters = {
  vesselName: [],
  imoNumber: [],
  yard: [],
  design: [],
  contractType: [],
  desk: [],
  builtYear: [],
  deliveryYear: [],
  redeliveryYear: [],
};

const matches = (selected: string[], value: string | null): boolean =>
  selected.length === 0 || Boolean(value && selected.includes(value));

const year = (value: string | null): string | null => value?.match(/\b\d{4}\b/)?.[0] ?? null;

const getFilterValue = (
  vessel: HullPaintsVessel,
  key: keyof HullPaintsFilters,
): string | null => {
  switch (key) {
    case "builtYear":
      return year(vessel.built);
    case "deliveryYear":
      return year(vessel.deliveryDate);
    case "redeliveryYear":
      return year(vessel.redeliveryDate);
    default:
      return vessel[key];
  }
};

const matchesFilters = (
  vessel: HullPaintsVessel,
  filters: HullPaintsFilters,
  ignoredKey?: keyof HullPaintsFilters,
): boolean =>
  (Object.keys(filters) as Array<keyof HullPaintsFilters>).every(
    (key) => key === ignoredKey || matches(filters[key], getFilterValue(vessel, key)),
  );

const compareNullableDatesDescending = (left: string | null, right: string | null): number => {
  const leftTime = left ? Date.parse(left) : Number.NaN;
  const rightTime = right ? Date.parse(right) : Number.NaN;

  if (!Number.isNaN(leftTime) || !Number.isNaN(rightTime)) {
    return (Number.isNaN(rightTime) ? -Infinity : rightTime) -
      (Number.isNaN(leftTime) ? -Infinity : leftTime);
  }

  return (right ?? "").localeCompare(left ?? "");
};

export const filterHullPaintsVessels = (
  vessels: HullPaintsVessel[],
  filters: HullPaintsFilters,
): HullPaintsVessel[] =>
  vessels
    .filter((vessel) => matchesFilters(vessel, filters))
    .sort((left, right) => compareNullableDatesDescending(left.lastRequest, right.lastRequest));

export const filterHullPaintsPaints = (
  paints: HullPaintsPaint[],
  visibleVessels: HullPaintsVessel[],
): HullPaintsPaint[] => {
  const visibleImos = new Set(visibleVessels.map((vessel) => vessel.imoNumber));

  // The source paint table has no explicit sort, so preserve API/model order.
  return paints.filter((paint) => visibleImos.has(paint.imoNumber));
};

export const getHullPaintsFilterOptions = (
  vessels: HullPaintsVessel[],
  filters: HullPaintsFilters,
  key: keyof HullPaintsFilters,
): string[] =>
  Array.from(
    new Set(
      [
        ...filters[key],
        ...vessels
          .filter((vessel) => matchesFilters(vessel, filters, key))
          .map((vessel) => getFilterValue(vessel, key))
          .filter((value): value is string => Boolean(value)),
      ],
    ),
  ).sort();
